import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { usePayrollSlipQuery } from "../api/queries";
import { PayslipPdfPreviewContent } from "../ui/PayslipPdfPreviewContent";
import { PayslipViewTour, usePayslipViewTour } from "../tour/PayslipViewTour";
import { Button } from "@devhop/ui";
import { ArrowLeftIcon, HelpCircle, PrinterIcon } from "lucide-react";
import { useEffect, useRef } from "react";

/** Safe filename: employee name + period (e.g. "MichaelChen_2025-01") */
function payslipFilename(employeeName: string, periodYear: number, periodMonth: number): string {
  const safe = employeeName.replace(/\s+/g, "").replace(/[^\p{L}\p{N}-]/gu, "") || "Payslip";
  const month = String(periodMonth).padStart(2, "0");
  return `${safe}_${periodYear}-${month}`;
}

export function PayslipViewPage() {
  const { id } = useParams({ from: "/app/hr/payroll/$id/slip" });
  const nav = useNavigate();
  const search = useSearch({ from: "/app/hr/payroll/$id/slip" });
  const query = usePayrollSlipQuery(id);
  const payroll = query.data;
  const hasTriggeredPrint = useRef(false);
  const tour = usePayslipViewTour();

  useEffect(() => {
    if (payroll?.employeeName != null) {
      const title = payslipFilename(
        payroll.employeeName,
        payroll.periodYear,
        payroll.periodMonth,
      );
      document.title = `${title} - ໃບເງິນເດືອນ`;
    }
    return () => {
      document.title = "freedom® Admin";
    };
  }, [payroll?.employeeName, payroll?.periodYear, payroll?.periodMonth]);

  // Auto-print when opened with ?print=1 (e.g. from detail page Print button)
  useEffect(() => {
    if (!payroll || !search.print || hasTriggeredPrint.current) return;
    hasTriggeredPrint.current = true;
    window.print();
    nav({
      to: "/app/hr/payroll/$id/slip",
      params: { id: id ?? "" },
      search: { print: undefined },
    });
  }, [payroll, search.print, id, nav]);

  const handleBack = () => {
    if (id) nav({ to: "/app/hr/payroll/$id/edit", params: { id } });
  };

  const handlePrint = () => {
    window.print();
  };

  if (query.isLoading || !id) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-primary border-b-2" />
              <p className="mt-2 text-muted-foreground">ກຳລັງໂຫຼດ...</p>
            </div>
          </div>
        </Main>
      </>
    );
  }

  if (query.isError || !payroll) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <h2 className="mb-2 font-bold text-2xl text-destructive">
                ບໍ່ພົບຂໍ້ມູນ ຫຼື ເກີດຂໍ້ຜິດພາດ
              </h2>
              <p className="mb-4 text-muted-foreground">
                ໃບເງິນເດືອນທີ່ທ່ານຊອກຫາບໍ່ມີຢູ່ ຫຼື ຖືກລຶບໄປແລ້ວ
              </p>
              <Button
                onClick={() =>
                  nav({
                    to: "/app/hr/payroll",
                    search: {
                      offset: 0,
                      limit: 20,
                      sort: undefined,
                      filters: undefined,
                      periodYear: undefined,
                      periodMonth: undefined,
                    },
                  })
                }
              >
                <ArrowLeftIcon className="mr-2 h-4 w-4" />
                ກັບຄືນລາຍການ
              </Button>
            </div>
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header />

      <Main>
        <PayslipViewTour run={tour.run} onCallback={tour.handleJoyrideCallback} />
        {/* Header Actions - Hidden in print (same as internal-audit AuditRecordPdfPreviewPage) */}
        <div
          className="mb-6 flex items-center justify-between print:hidden"
          data-tourid="payslip-view-actions"
        >
          <div className="flex items-center space-x-2">
            <Button variant="ghost" onClick={handleBack} className="gap-2">
              <ArrowLeftIcon className="h-4 w-4" />
              ກັບຄືນ
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="size-7 rounded-full"
              onClick={tour.startTour}
              title="ເລີ່ມທົດສອບການນຳທາງ"
              aria-label="ເລີ່ມທົດສອບການນຳທາງ"
            >
              <HelpCircle className="size-5" />
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handlePrint} className="gap-2">
              <PrinterIcon className="h-4 w-4" />
              ພິມ
            </Button>
          </div>
        </div>

        {/* PDF Preview Content (same layout/format as internal-audit) */}
        <div data-tourid="payslip-view-content">
          <PayslipPdfPreviewContent payroll={payroll} />
        </div>
      </Main>
    </>
  );
}
